
import { AffiliateSubscription } from "@/interfaces/AffiliateSubscription";
import { MarketplaceSubscriptionChargeQueue } from "@/interfaces/MarketplaceSubscriptionChargeQueue";
import { PatientAffiliateSubscription } from "@/interfaces/PatientAffiliateSubscription";
import Sidebar from "primevue/sidebar";
import Timeline from "primevue/timeline";
import { mapGetters, mapMutations } from "vuex";
import { subscriptionPaymentFlowScreenView } from "@/interfaces/SubscriptionPaymentFlowEnum";
import { PatientAffiliateSubscriptionStatusIDEnum } from "@/interfaces/PatientAffiliateSubscriptionStatusIDEnum";
import { PatientAffiliateSubscriptionUpdate } from "@/interfaces/PatientAffiliateSubscriptionUpdate";
import { MarketplaceSubscriptionChargeStatusEnum } from "@/interfaces/MarketplaceSubscriptionChargeStatusEnum";
import FamilyPlanManager from "@/components/FamilyPlanManagerComponent.vue";
import swal from "sweetalert";

export default {
  name: "SubscriptionPricingCards",
  props: {
    affiliateSubscriptions: {
      type: Array as () => AffiliateSubscription[],
      required: true,
    },
    patientAffiliateSubscriptions: {
      type: Array as () => PatientAffiliateSubscription[],
      required: true,
    },
    subscriptionCharges: {
      type: Array as () => MarketplaceSubscriptionChargeQueue[],
      required: true,
    },
    familyPlanManagerFullName: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      isMonthly: true,
      showPaymentHistory: false,
      hasBypassedAssociateWarning: false,
    };
  },
  computed: {
    ...mapGetters({
      SignedIn: "getSignedIn",
    }),

    currentSubscription(): { subscription: AffiliateSubscription | null; isPaymentError: boolean } {
      if (!this.affiliateSubscriptions || !this.patientAffiliateSubscriptions) {
        return { subscription: null, isPaymentError: false };
      }

      // Find active "Current" subscription
      const activePatientSub = this.patientAffiliateSubscriptions.find((sub) => sub.patientAffiliateSubscriptionStatusID === PatientAffiliateSubscriptionStatusIDEnum.Current && sub.active === true);

      if (activePatientSub) {
        const matchedAffiliateSub = this.affiliateSubscriptions.find((affSub) => affSub.id === activePatientSub.affiliateSubscriptionID);
        return { subscription: matchedAffiliateSub || null, isPaymentError: false };
      }

      // No "Current" subscription, try to find a "PaymentError" subscription
      const failedPaymentPatientSub = this.patientAffiliateSubscriptions.find((sub) => sub.patientAffiliateSubscriptionStatusID === PatientAffiliateSubscriptionStatusIDEnum.PaymentError && sub.active === true);

      if (failedPaymentPatientSub) {
        const matchedAffiliateSub = this.affiliateSubscriptions.find((affSub) => affSub.id === failedPaymentPatientSub.affiliateSubscriptionID);
        return { subscription: matchedAffiliateSub || null, isPaymentError: true };
      }

      // No matching subscription found
      return { subscription: null, isPaymentError: false };
    },

    currentPatientSubscription(): PatientAffiliateSubscription | null {
      if (!this.patientAffiliateSubscriptions) {
        return null;
      }

      // Find active "Current" subscription
      const activePatientSub = this.patientAffiliateSubscriptions.find((sub) => sub.active === true);

      return activePatientSub ? activePatientSub : null;
    },

    startDate(): string | null {
      if (!this.affiliateSubscriptions || !this.patientAffiliateSubscriptions) {
        return null;
      }

      // Find active subscription with status ID = 0
      const activePatientSub = this.patientAffiliateSubscriptions.find((sub) => sub.active === true);

      if (!activePatientSub) {
        return null;
      }

      return activePatientSub.createdDate || null;
    },

    hasPaidPlan(): boolean {
      const sub = this.currentSubscription.subscription;
      return sub ? sub.monthlyPrice > 0 || sub.yearlyPrice > 0 : false;
    },

    isFamilyPlanManager(): boolean {
      return this.currentSubscription.subscription?.isFamilyPlan === true && this.currentPatientSubscription.isMainUser === true;
    },

    isFamilyPlanAssociate(): boolean {
      return this.currentSubscription.subscription?.isFamilyPlan === true && this.currentPatientSubscription.isMainUser === false;
    },

    futureChargeDate(): string | null {
      const sub = this.currentSubscription.subscription;
      if (!sub || !this.hasPaidPlan || !this.subscriptionCharges) {
        return null;
      }

      const matchingCharge = this.subscriptionCharges.find((charge) => charge.affiliateSubscriptionID === sub.id && charge.marketplaceSubscriptionChargeStatusID === MarketplaceSubscriptionChargeStatusEnum.Scheduled);

      return matchingCharge ? matchingCharge.chargeDate : null;
    },

    failedChargeDate(): string | null {
      const sub = this.currentSubscription.subscription;
      if (!sub || !this.hasPaidPlan || !this.subscriptionCharges) {
        return null;
      }

      const matchingCharge = this.subscriptionCharges.find((charge) => charge.affiliateSubscriptionID === sub.id && charge.marketplaceSubscriptionChargeStatusID === MarketplaceSubscriptionChargeStatusEnum.PaymentError);

      return matchingCharge ? matchingCharge.chargeDate : null;
    },

    gracePeriodDaysLeft(): number | null {
      const sub = this.currentSubscription?.subscription;
      const failedDateStr = this.failedChargeDate;

      if (!sub || !failedDateStr || !sub.gracePeriod) {
        return null;
      }

      const gracePeriod = sub.gracePeriod;
      const failedDate = new Date(failedDateStr);
      const today = new Date();

      // Calculate how many days have passed since the failed charge
      const diffTime = today.getTime() - failedDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to full days

      // Remaining grace period days
      const daysLeft = gracePeriod - diffDays;

      // Don't return negative numbers, cap at 0
      return daysLeft > 0 ? daysLeft : 0;
    },

    visibleSubscriptionCards() {
      const selectedPlan = this.currentSubscription.subscription;

      // If no plan selected or selected is free — show everything
      if (!selectedPlan || (selectedPlan.monthlyPrice === 0 && selectedPlan.yearlyPrice === 0)) {
        return this.affiliateSubscriptions;
      }

      // If a paid plan is selected — hide the free plan
      return this.affiliateSubscriptions.filter((sub) => sub.monthlyPrice > 0 || sub.yearlyPrice > 0);
    },
    centerCardsIfTwoOrLess(): boolean {
      return this.visibleSubscriptionCards.length <= 2;
    },

    nextTierUp() {
      // Make sure the current user subscription and available subscriptions exist.
      if (!this.currentSubscription.subscription || !this.affiliateSubscriptions) {
        return null;
      }

      const currentTier = this.currentSubscription.subscription.tier;

      // Filter subscriptions that have a tier higher than the user's current subscription.
      const higherSubs = this.affiliateSubscriptions.filter((sub) => sub.tier > currentTier);

      // If no subscription has a higher tier, the user is at the best level.
      if (higherSubs.length === 0) {
        return null;
      }

      // Sort the higher subscriptions in ascending order by tier.
      higherSubs.sort((a, b) => a.tier - b.tier);

      // Return the one with the smallest tier among those above the user's tier.
      return higherSubs[0];
    },

    affiliateDisplayNames() {
      const map = {};
      if (!this.affiliateSubscriptions) return map;

      this.affiliateSubscriptions.forEach((sub) => {
        map[sub.id] = sub.displayName;
      });
      return map;
    },
    customMarketplaceStatusLabels() {
      return {
        [MarketplaceSubscriptionChargeStatusEnum.Scheduled]: "Scheduled Charge",
        [MarketplaceSubscriptionChargeStatusEnum.PaymentProcessed]: "Successful Payment",
        [MarketplaceSubscriptionChargeStatusEnum.PaymentError]: "Payment Failed",
        [MarketplaceSubscriptionChargeStatusEnum.Canceled]: "Subscription Canceled",
        [MarketplaceSubscriptionChargeStatusEnum.Refund]: "Refund Issued",
      };
    },
    sortedSubscriptionCharges() {
      if (!this.subscriptionCharges) return [];

      return [...this.subscriptionCharges].filter((charge) => charge.marketplaceSubscriptionChargeStatusID !== MarketplaceSubscriptionChargeStatusEnum.Canceled).sort((a, b) => new Date(b.chargeDate) - new Date(a.chargeDate));
    },
  },
  methods: {
    ...mapMutations({
      setShowSubscriptionPaymentFlow: "setShowSubscriptionPaymentFlow",
      setPatientAffiliateSubscriptionUpdate: "setPatientAffiliateSubscriptionUpdate",
      setShowSubscriptionManagement: "setShowSubscriptionManagement",
    }),
    isBillingMonthly(isMonthly: boolean) {
      this.isMonthly = isMonthly;
    },
    showPayment() {
      this.showPaymentHistory = true;
    },
    safeParse(json: string) {
      try {
        return JSON.parse(json);
      } catch (e) {
        return [];
      }
    },
    formatDate(dateStr: string): string {
      const date = new Date(dateStr);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    },
    async confirmAction(message: string): Promise<boolean> {
      return swal({
        title: "Are you sure?",
        text: message,
        icon: "warning",
        buttons: ["Cancel", "Yes"],
        dangerMode: true,
      });
    },
    async openCart(s: AffiliateSubscription) {
      if (this.isFamilyPlanAssociate && !this.hasBypassedAssociateWarning) {
        const confirmed = await this.confirmAction(`Are you sure that you want to switch to a different subscription? You will lose access your current plan.`);
        if (!confirmed) return;

        this.hasBypassedAssociateWarning = true;
      }

      if (this.isFamilyPlanManager && !this.hasBypassedAssociateWarning) {
        const confirmed = await this.confirmAction(`Are you sure that you want to switch to a different subscription? If you switch to a non-family plan, all of your existing members will lose access to their subscription and be downgraded to a free plan if available.`);
        if (!confirmed) return;

        this.hasBypassedAssociateWarning = true;
      }

      const payload: PatientAffiliateSubscriptionUpdate = {
        affiliateSubscriptionID: s.id,
        isMonthly: this.isMonthly,
        isFailedPaymentUpdate: false,
      };
      this.setShowSubscriptionManagement(false);
      this.setPatientAffiliateSubscriptionUpdate(payload);
      this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.checkout, forceFlow: false });
    },

    openPaymentUpdateCart() {
      const payload: PatientAffiliateSubscriptionUpdate = {
        affiliateSubscriptionID: this.currentSubscription.subscription.id,
        isMonthly: this.currentPatientSubscription?.subscriptionFrequencyDays === 30,
        isFailedPaymentUpdate: true,
      };

      this.setPatientAffiliateSubscriptionUpdate(payload);
      this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.checkout, forceFlow: false });
    },

    openUpgradeSelection() {
      if (this.nextTierUp != null) {
        const payload: PatientAffiliateSubscriptionUpdate = {
          affiliateSubscriptionID: this.nextTierUp.id,
          isMonthly: this.isMonthly,
        };
        this.setShowSubscriptionManagement(false);
        this.setPatientAffiliateSubscriptionUpdate(payload);
        this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
      }
    },
    async openDowngradeSelection() {
      if (this.isFamilyPlanAssociate && !this.hasBypassedAssociateWarning) {
        const confirmed = await this.confirmAction(`Are you sure that you want to switch to a different subscription? You will lose access your current plan.`);
        if (!confirmed) return;

        this.hasBypassedAssociateWarning = true;
      }

      if (this.isFamilyPlanManager && !this.hasBypassedAssociateWarning) {
        const confirmed = await this.confirmAction(`Are you sure that you want to switch to a different subscription? If you switch to a non-family plan, all of your existing members will lose access to their subscription and be downgraded to a free plan if available.`);
        if (!confirmed) return;

        this.hasBypassedAssociateWarning = true;
      }

      const payload: PatientAffiliateSubscriptionUpdate = {
        affiliateSubscriptionID: this.currentSubscription.subscription.id,
        isMonthly: this.isMonthly,
      };

      this.setShowSubscriptionManagement(false);
      this.setPatientAffiliateSubscriptionUpdate(payload);
      this.setShowSubscriptionPaymentFlow({ show: true, view: subscriptionPaymentFlowScreenView.selectProgram, forceFlow: false });
    },

    openFamilyManager() {
      this.$bvModal.show("family-manager-modal");
    },
  },
  components: {
    Sidebar,
    Timeline,
    FamilyPlanManager,
  },
};
